/**
 * useInactivityTimer - Tracks user inactivity for screensaver
 * 
 * Features:
 * - Detects touch, mouse, keyboard, and visibility events
 * - Resets timer on any user interaction
 * - Configurable timeout in minutes
 * - Returns isScreensaverActive flag
 * 
 * CRITICAL: Works on all devices including iOS/Safari
 */
import { useState, useEffect, useRef, useCallback } from 'react';

export function useInactivityTimer({ timeoutMinutes = 5 } = {}) {
  const [isScreensaverActive, setIsScreensaverActive] = useState(false);
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsScreensaverActive(false);

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Set new timer
    timerRef.current = setTimeout(() => {
      console.log('[useInactivityTimer] Timeout reached, activating screensaver');
      setIsScreensaverActive(true);
    }, timeoutMinutes * 60 * 1000);
  }, [timeoutMinutes]);

  const forceActivate = useCallback(() => {
    console.log('[useInactivityTimer] Force activating screensaver');
    setIsScreensaverActive(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Event listeners for user activity
    // Using comprehensive event list for iOS/Safari compatibility
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'touchstart',
      'touchmove',
      'pointerdown',
      'scroll',
      'wheel'
    ];

    const handleActivity = () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      
      // Debounce: only reset if >1s since last activity
      if (timeSinceLastActivity > 1000) {
        if (isScreensaverActive) {
          console.log('[useInactivityTimer] User activity detected, deactivating screensaver');
        }
        resetTimer();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('[useInactivityTimer] Tab became visible, resetting timer');
        resetTimer();
      }
    };

    // Start initial timer
    resetTimer();

    // Attach listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [timeoutMinutes, isScreensaverActive, resetTimer]);

  return {
    isScreensaverActive,
    isInactive: isScreensaverActive, // Alias for backward compatibility
    resetTimer,
    forceActivate
  };
}
